import React from 'react';
import { Link } from 'react-router-dom';
import './LegalPage.css';

const Terms = () => (
  <div className="legal-page">
    <Link to="/" className="legal-back">← Back to home</Link>
    <header className="legal-header">
      <h1 className="legal-title">Terms and Conditions</h1>
      <p className="legal-meta">Persona Sports · Last Updated: September 27, 2026 · Effective Date: September 27, 2026</p>
    </header>
    <div className="legal-content">
      <p>Please read these Terms and Conditions ("Terms") carefully before using the Persona Sports mobile application (the "App") operated by Persona Sports ("we", "us", "our").</p>
      <p>By creating an account or using the App, you agree to be bound by these Terms. If you do not agree, do not use the App.</p>

      <h2>1. THE SERVICE</h2>
      <h3>1.1 Description</h3>
      <p>Persona Sports is a mobile application that provides AI-powered sports conversations. The App uses artificial intelligence to generate responses about sports teams, players, and events. Responses are generated in the persona of the selected team to create an engaging fan experience.</p>
      <h3>1.2 AI-Generated Content</h3>
      <p>All chat responses are generated by artificial intelligence (Google Gemini). While we strive for accuracy:</p>
      <ul>
        <li>AI responses may contain errors, inaccuracies, or outdated information.</li>
        <li>AI responses are for entertainment and informational purposes only.</li>
        <li>AI responses do not constitute professional advice of any kind.</li>
        <li>We do not guarantee the accuracy, completeness, or reliability of any AI-generated content.</li>
        <li>You should independently verify any information before relying on it.</li>
      </ul>
      <h3>1.3 External Sources</h3>
      <p>The App may retrieve information from publicly available web sources to provide current responses. We do not control or guarantee the accuracy of these external sources.</p>

      <h2>2. ACCOUNTS</h2>
      <h3>2.1 Registration</h3>
      <p>To use the App beyond the limited guest experience, you must create an account using a valid email address and password, or sign in with Apple or Google. You are responsible for:</p>
      <ul>
        <li>Providing accurate registration information.</li>
        <li>Maintaining the confidentiality of your account credentials.</li>
        <li>All activities that occur under your account.</li>
      </ul>
      <h3>2.2 Account Security</h3>
      <p>You must notify us immediately of any unauthorised access to your account. We are not liable for any loss or damage arising from your failure to protect your account credentials.</p>
      <h3>2.3 Profile Information</h3>
      <p>You may optionally set a username and display name. Usernames must:</p>
      <ul>
        <li>Be at least 3 characters long.</li>
        <li>Not impersonate other individuals or entities.</li>
        <li>Not contain offensive, misleading, or inappropriate content.</li>
      </ul>
      <p>We reserve the right to remove or modify usernames that violate these requirements.</p>

      <h2>3. ACCEPTABLE USE</h2>
      <h3>3.1 You Agree Not To:</h3>
      <ul>
        <li>Use the App for any unlawful purpose or in violation of any applicable laws.</li>
        <li>Attempt to bypass, circumvent, or manipulate the App's usage limits or security measures.</li>
        <li>Use automated scripts, bots, or other means to interact with the App outside of its intended interface.</li>
        <li>Transmit harmful, threatening, abusive, defamatory, or otherwise objectionable content through the chat.</li>
        <li>Attempt to extract, scrape, or systematically collect data from the App.</li>
        <li>Reverse engineer, decompile, or disassemble any part of the App.</li>
        <li>Use the App to generate content that promotes hate speech, violence, or discrimination.</li>
        <li>Resell, redistribute, or commercially exploit the App or its content without our prior written consent.</li>
        <li>Interfere with or disrupt the integrity or performance of the App or its related systems.</li>
        <li>Generate fraudulent ad impressions or clicks, use automated tools, emulators, or multiple accounts to claim rewards, or otherwise manipulate the rewarded advertising feature.</li>
      </ul>
      <h3>3.2 Enforcement</h3>
      <p>We reserve the right to suspend or terminate your account, without notice, if we reasonably believe you have violated these Terms.</p>

      <h2>4. USAGE LIMITS</h2>
      <h3>4.1 Message Limits</h3>
      <p>The App enforces fair usage limits on the number of messages you can send within a given time period. These limits are in place to ensure service availability for all users and to manage operational costs.</p>
      <h3>4.2 Current Limits</h3>
      <ul>
        <li>Users are allocated a set number of messages per session.</li>
        <li>When the limit is reached, a cooldown period applies before additional messages can be sent.</li>
        <li>Specific limit amounts and cooldown durations may change at our discretion.</li>
      </ul>
      <h3>4.3 Rewarded Advertisements</h3>
      <p>When you reach your message limit, signed-in users may choose to watch a rewarded video advertisement to receive additional messages. By using this feature, you agree that:</p>
      <ul>
        <li>Watching a rewarded advertisement is entirely optional. The App works without it; the standard cooldown still applies if you choose not to watch.</li>
        <li>A completed view currently grants 10 additional messages, available once per limit period. The reward is granted only after the advertisement is completed and the view is confirmed by our advertising partner (Google AdMob).</li>
        <li>Rewards have no monetary value, cannot be exchanged for cash or any other benefit, cannot be transferred between accounts, and expire when your limit period resets.</li>
        <li>Advertisement availability is not guaranteed. If no advertisement is available, or a view cannot be confirmed, no reward is granted.</li>
        <li>We may change the reward amount, frequency, or eligibility, or withdraw the feature, at any time.</li>
        <li>We may withhold rewards or suspend your account if we reasonably suspect abuse of this feature (see section 3.1).</li>
      </ul>

      <h2>4A. ADVERTISING</h2>
      <p>The App displays advertisements served by Google AdMob, including banner advertisements and optional rewarded video advertisements. Advertisements are provided by third parties. We do not endorse, and are not responsible for, the products, services, or content of advertisers, and any dealings you have with advertisers are solely between you and them. How advertising data is handled is described in our Privacy Policy.</p>

      <h2>5. INTELLECTUAL PROPERTY</h2>
      <h3>5.1 Our Property</h3>
      <p>The App, including its design, code, features, graphics, and branding, is owned by us and is protected by intellectual property laws. You may not copy, modify, distribute, or create derivative works based on the App without our prior written consent.</p>
      <h3>5.2 Your Content</h3>
      <p>You retain ownership of any content you input into the App (e.g., chat messages). By using the App, you grant us a limited, non-exclusive licence to process your input solely for the purpose of providing the service (i.e., generating AI responses).</p>
      <h3>5.3 AI-Generated Content</h3>
      <p>AI-generated responses are provided for your personal use. You may share or reference them, but you may not systematically reproduce or commercially exploit large volumes of AI-generated content from the App.</p>
      <h3>5.4 Third-Party Trademarks</h3>
      <p>Sports team names, logos, and related marks referenced within the App are the property of their respective owners. Their appearance in the App does not imply endorsement or affiliation.</p>

      <h2>6. DISCLAIMERS</h2>
      <h3>6.1 "As Is" Basis</h3>
      <p>THE APP IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.</p>
      <h3>6.2 No Guarantee of Availability</h3>
      <p>We do not guarantee that the App will be available at all times or without interruption. We may modify, suspend, or discontinue the App (or any part of it) at any time without notice.</p>
      <h3>6.3 Third-Party Services</h3>
      <p>The App relies on third-party services (including AI providers, advertising networks, hosting platforms, and data sources). We are not responsible for the availability, accuracy, or performance of these third-party services.</p>

      <h2>7. LIMITATION OF LIABILITY</h2>
      <p>TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW:</p>
      <ul>
        <li>WE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING FROM YOUR USE OF THE APP.</li>
        <li>OUR TOTAL LIABILITY TO YOU FOR ANY CLAIMS ARISING FROM OR RELATED TO THE APP SHALL NOT EXCEED THE AMOUNT YOU HAVE PAID US IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM, OR TEN POUNDS STERLING (GBP 10), WHICHEVER IS GREATER.</li>
        <li>WE ARE NOT LIABLE FOR ANY DECISIONS OR ACTIONS YOU TAKE BASED ON AI-GENERATED CONTENT.</li>
      </ul>

      <h2>8. INDEMNIFICATION</h2>
      <p>You agree to indemnify and hold us harmless from any claims, damages, losses, liabilities, and expenses (including legal fees) arising from:</p>
      <ul>
        <li>Your use of the App.</li>
        <li>Your violation of these Terms.</li>
        <li>Your violation of any third-party rights.</li>
      </ul>

      <h2>9. TERMINATION</h2>
      <h3>9.1 By You</h3>
      <p>You may stop using the App and request deletion of your account at any time.</p>
      <h3>9.2 By Us</h3>
      <p>We may suspend or terminate your access to the App at any time, with or without cause, and with or without notice. Reasons for termination may include, but are not limited to, violation of these Terms or abusive behaviour.</p>
      <h3>9.3 Effect of Termination</h3>
      <p>Upon termination, your right to use the App ceases immediately. Sections of these Terms that by their nature should survive termination (including Disclaimers, Limitation of Liability, and Indemnification) shall continue to apply.</p>

      <h2>10. GOVERNING LAW AND DISPUTES</h2>
      <p>These Terms shall be governed by and construed in accordance with the laws of England and Wales. Any disputes arising from these Terms or your use of the App shall be subject to the exclusive jurisdiction of the courts of England and Wales.</p>

      <h2>11. CHANGES TO THESE TERMS</h2>
      <p>We reserve the right to modify these Terms at any time. We will notify you of material changes by posting updated Terms within the App and updating the "Last Updated" date. Your continued use of the App after such changes constitutes your acceptance of the revised Terms.</p>

      <h2>12. SEVERABILITY</h2>
      <p>If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited or eliminated to the minimum extent necessary, and the remaining provisions shall remain in full force and effect.</p>

      <h2>13. ENTIRE AGREEMENT</h2>
      <p>These Terms, together with our Privacy Policy, constitute the entire agreement between you and us regarding your use of the App and supersede any prior agreements.</p>

      <h2>14. CONTACT US</h2>
      <p>If you have any questions about these Terms, please contact us at:</p>
      <p><strong>Email:</strong> <a href="mailto:support@svarapp.com">support@svarapp.com</a><br /><strong>Website:</strong> <a href="https://svarapp.com" target="_blank" rel="noopener noreferrer">https://svarapp.com</a></p>
    </div>
  </div>
);

export default Terms;
